import { useContext, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { styled } from 'styled-components';
import moment from 'moment';
import * as uuid from 'uuid';

import { Button, Modal } from 'react-bootstrap';

import * as api from '../../../../../../api';

import type { ChatConversation, StyledButton } from '../../../../../../common';
import {
  ContinueButton,
  ErrorHint,
  InputLabel,
  ReconciliationContainer,
} from './common';
import { ConfigurationContext } from '../../../../../../context/configurationContext';
import { isNumberValid } from '../../../../../../utils/phoneNumber';
import MessageContent, { getMessageContent } from '../../messageContent';
import { ConversationContext } from '../../../../../../context/conversationContext';

import { ReactComponent as CloseIconSvg } from '../../../../../../assets/close_icon.svg';
import ConfigurableIcon from '../../../../ConfigurableIcon';

const PCMA_HEALTH_ASSISTANT_ID = 'e349e3d3-6ed2-46e3-a7bb-5ccfc19c7605';

interface Props {
  message: ChatConversation;
  onContactVerificationRequired: (
    provisionalPatientId: string,
    loginInfo: api.AuthPatient.AuthPatientBody,
  ) => void;
  showTextAnimation: boolean;
  showMessageAnswer?: boolean;
  onReconciliationSuccess: (
    fallback?: Pick<
      api.AuthPatient.AuthPatientBody,
      'firstName' | 'lastName' | 'dateOfBirth'
    >,
  ) => void;
}

interface ReconciliationValues {
  firstName: string;
  lastName: string;
  dob: string;
  phoneNumber: string;
  email: string;
}

const validEmailRegex =
  // eslint-disable-next-line no-control-regex
  /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i;

const Authentication: React.FC<Props> = ({
  message,
  onContactVerificationRequired,
  showTextAnimation,
  showMessageAnswer,
  onReconciliationSuccess,
}) => {
  const configurationContext = useContext(ConfigurationContext);
  const conversationContext = useContext(ConversationContext);
  const [openedWindow, setOpenedWindow] = useState<Window | null>(null);
  const [formValues, setFormValues] = useState<Partial<ReconciliationValues>>(
    'user_entities' in message
      ? {
          firstName: message.user_entities.user_first_name,
          lastName: message.user_entities.user_last_name,
          dob:
            message.user_entities.user_dob &&
            moment(message.user_entities.user_dob).isValid()
              ? moment(message.user_entities.user_dob)
                  .toISOString()
                  .substring(0, 10)
              : undefined,
          phoneNumber: message.user_entities.user_phone_number,
          email: message.user_entities.user_email,
        }
      : {},
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!openedWindow) return;
    const bc = new BroadcastChannel('sso');
    bc.onmessage = (event) => {
      if (event.data?.action !== 'closeWindow') return;
      onReconciliationSuccess();
    };
  }, [openedWindow, onReconciliationSuccess]);

  useEffect(() => {
    if (!('user_entities' in message)) return;
    setFormValues({
      firstName: message.user_entities.user_first_name,
      lastName: message.user_entities.user_last_name,
      dob:
        message.user_entities.user_dob &&
        moment(message.user_entities.user_dob).isValid()
          ? moment(message.user_entities.user_dob)
              .toISOString()
              .substring(0, 10)
          : undefined,
      phoneNumber: message.user_entities.user_phone_number,
      email: message.user_entities.user_email,
    });
  }, [message]);

  const handleSubmit = async (
    values: ReconciliationValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    if (!configurationContext?.organizationId) return;
    if (!configurationContext.setAuthenticated) return;
    if (!configurationContext.id) return;
    if (!configurationContext.sessionId) return;
    const submittedValues = {
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: new Date(values.dob),
      cellPhoneNumber: values.phoneNumber,
      email: values.email.toLowerCase(),
    };
    try {
      const { contactVerification, provisionalPatient } =
        await api.client.Previsit.authPatientForAssistant({
          loginInfo: submittedValues,
          configurationId: configurationContext.id,
          sessionId: configurationContext.sessionId,
        });
      if (contactVerification) {
        onContactVerificationRequired(provisionalPatient.id, submittedValues);
      }
    } catch (err) {
      console.error(err);
      if (err.status === 429 && conversationContext) {
        conversationContext.addNewMessage({
          chat_message_id: uuid.v4(),
          source_documents: [],
          response_entities: {
            recommendation: api.Assistant.Recommendation.Authentication,
          },
          user_entities: {},
          done: true,
          answer:
            "Oops! It looks like you've exceeded the rate limit for authentication using " +
            'your phone number or email. For security purposes, we limit the number of ' +
            'authentication attempts you can make in a day, but you can also try using a ' +
            'different phone number or email.',
          intent:
            'intent' in message
              ? message.intent
              : api.Assistant.Intent.GeneralInformation,
          intentComponent: undefined,
        });
      }
    }
    setSubmitting(false);
  };

  const handleSsoAuth = () => {
    if (!configurationContext?.organizationId) return;
    if (!configurationContext.sessionId) return;
    const url = api.client.common.constructPath(
      `/patient-previsit/auth/reconciliation/organizations/${configurationContext.organizationId}/patients/sso`,
      {
        sessionId: configurationContext.sessionId,
        assistantId: configurationContext.id,
      },
    );
    const baseUrl =
      document.location.hostname === 'localhost'
        ? 'http://localhost:6363'
        : document.location.origin;
    setOpenedWindow(window.open(`${baseUrl}${url}`, '_blank'));
  };

  return (
    <>
      <MessageContent
        content={
          showMessageAnswer
            ? getMessageContent(message)
            : "I can help you do that. Let's check your records. I just need some details to get started."
        }
        showTextAnimation={true}
        isAgent={true}
        isLoading={false}
      />

      <ReconciliationContainer
        $delaySecond={0.3}
        data-test-id="authentication-form"
      >
        <Formik
          initialValues={{
            firstName: formValues.firstName ?? '',
            lastName: formValues.lastName ?? '',
            dob: formValues.dob ?? '',
            phoneNumber: formValues.phoneNumber ?? '',
            email: formValues.email ?? '',
          }}
          onSubmit={handleSubmit}
          validate={(values) => {
            const emailError =
              values.email && !validEmailRegex.test(values.email)
                ? 'Invalid email address'
                : undefined;
            const phoneNumberError =
              values.phoneNumber && !isNumberValid(values.phoneNumber)
                ? 'Invalid phone number'
                : undefined;

            const errors = {
              email: emailError,
              phoneNumber: phoneNumberError,
            };
            if (Object.values(errors).some((error) => error !== undefined)) {
              return {
                email: emailError,
                phoneNumber: phoneNumberError,
              };
            }
          }}
        >
          {({ isSubmitting, values, handleChange }) => (
            <Form>
              <div className="row">
                <div className="col-sm-6 mb-2">
                  <InputLabel htmlFor="firstName">
                    Patient First Name
                  </InputLabel>
                  <input
                    data-test-id="authentication-first-name-input"
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="form-control"
                    value={values.firstName}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting || message.disabled}
                  />
                </div>
                <div className="col-sm-6 mb-2">
                  <InputLabel htmlFor="lastName">Patient Last Name</InputLabel>
                  <input
                    data-test-id="authentication-last-name-input"
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="form-control"
                    value={values.lastName}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting || message.disabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6 mb-2">
                  <InputLabel htmlFor="dob">Patient Date of Birth</InputLabel>
                  <input
                    data-test-id="authentication-dob-input"
                    type="date"
                    id="dob"
                    name="dob"
                    className="form-control"
                    max={moment().format('YYYY-MM-DD')}
                    value={values.dob}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting || message.disabled}
                  />
                  <ErrorHint name="dob" component="div" />
                </div>
                <div className="col-sm-6 mb-2">
                  <InputLabel htmlFor="phoneNumber">
                    Patient Phone Number
                  </InputLabel>
                  <input
                    data-test-id="authentication-phone-number-input"
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    className="form-control"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting || message.disabled}
                  />
                  <ErrorHint name="phoneNumber" component="div" />
                </div>
              </div>
              <div className="row">
                <div className="col mb-3">
                  <InputLabel htmlFor="email">Patient Email Address</InputLabel>
                  <input
                    data-test-id="authentication-email-input"
                    type="email"
                    id="email"
                    name="email"
                    className="form-control"
                    value={values.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting || message.disabled}
                  />
                  <ErrorHint name="email" component="div" />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <ContinueButton
                    disabled={isSubmitting || message.disabled}
                    type="submit"
                  >
                    Continue
                  </ContinueButton>
                </div>
              </div>
            </Form>
          )}
        </Formik>
        {configurationContext?.ssoAuthEnabled ? (
          <>
            <Divider>
              <DividerText>or</DividerText>
            </Divider>
            <SsoAuthButton
              variant="outline-primary"
              onClick={() =>
                configurationContext?.ssoEnabled &&
                configurationContext.id !== PCMA_HEALTH_ASSISTANT_ID
                  ? handleSsoAuth()
                  : setIsModalOpen(true)
              }
            >
              <ConfigurableIcon
                iconName={configurationContext.ssoAuthButtonIcon ?? undefined}
                organizationId={configurationContext.organizationId}
                margin="0 8px 0 0"
                color={'#3f3f3f'}
              />
              {configurationContext.ssoAuthButtonLabel ?? 'Log in with MyChart'}
            </SsoAuthButton>
          </>
        ) : null}
      </ReconciliationContainer>
      <ModalWrapper
        centered
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
      >
        <ModalHeader>
          <ModalTitle>Log in with MyChart</ModalTitle>
          <CloseButton type="button" onClick={() => setIsModalOpen(false)}>
            <CloseIconSvg />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          Learn more about how Assistant can support Epic single sign-on by
          scheduling a demo with a product specialist{' '}
          <a
            href="https://www.notablehealth.com/request-a-demo"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          .
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => setIsModalOpen(false)}>
            Done
          </Button>
        </ModalFooter>
      </ModalWrapper>
    </>
  );
};

const SsoAuthButton = styled<StyledButton>(Button)`
  width: 100%;
  color: #3f3f3f !important;
  border-color: #bfbfbf !important;
  background-color: transparent !important;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  font-weight: bold;

  span {
    padding-right: 8px;
    border-right: 1px solid var(--color-border-default-light);

    svg {
      max-height: 24px;
    }
  }
`;

const Divider = styled.div`
  color: var(--color-shade-80);
  font-size: 15px;
  text-align: center;
  margin: 12px 0;
  position: relative;

  &::before {
    content: '';
    top: 12px;
    left: 0;
    right: 0;
    position: absolute;
    height: 1px;
    background-color: var(--color-shade-50);
  }
`;

const DividerText = styled.span`
  position: relative;
  padding: 0 20px;
  background-color: var(--color-shade-0);
`;

const ModalWrapper = styled(Modal)`
  z-index: 100005;
`;

const ModalHeader = styled(Modal.Header)`
  padding-bottom: 5px;
  padding-top: 5px;
`;

const ModalTitle = styled(Modal.Title)`
  font-size: 14px;
  font-weight: bold;
`;

const CloseButton = styled.button`
  border: none;
  background-color: transparent;
`;

const ModalBody = styled(Modal.Body)`
  font-size: 14px;
`;

const ModalFooter = styled(Modal.Footer)`
  border-top: none;

  > button {
    font-size: 14px;
  }
`;

export default Authentication;
